import React, { useEffect, useState, useCallback } from "react";
import {
  ListChecks,
  Search,
  Filter,
  AlertCircle,
  CheckCircle2,
  Info,
} from "lucide-react";
import LoadingSpinner from "../../common/LoadingSpinner";
import { auditLogsAPI } from "../../../services/api";

const PAGE_SIZE = 50;

const GlobalAuditLogView = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [resultFilter, setResultFilter] = useState("all");
  const [entityFilter, setEntityFilter] = useState("all");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        limit: PAGE_SIZE,
        offset: (page - 1) * PAGE_SIZE,
      };
      if (search.trim()) params.q = search.trim();
      if (resultFilter !== "all") params.result = resultFilter;
      if (entityFilter !== "all") params.entity_type = entityFilter;

      const res = await auditLogsAPI.getAll(params);
      const data = res.data || {};
      setLogs(Array.isArray(data.items) ? data.items : []);
      setTotal(data.total || 0);
    } catch (e) {
      console.error("Failed to load audit logs", e);
      setError(e);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [page, search, resultFilter, entityFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setPage(1);
    load();
  };

  const resultsBadge = (result) => {
    const r = (result || "").toLowerCase();
    if (r === "success") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-emerald-50 text-emerald-700">
          <CheckCircle2 className="w-3 h-3 mr-1" />
          Success
        </span>
      );
    }
    if (r === "fail" || r === "error") {
      return (
        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-red-50 text-red-700">
          <AlertCircle className="w-3 h-3 mr-1" />
          Failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700">
        <Info className="w-3 h-3 mr-1" />
        {result || "Unknown"}
      </span>
    );
  };

  const entityBadge = (log) => {
    const label = log.entity_type_label || log.entity_type || "Item";
    return (
      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs bg-blue-50 text-blue-700">
        {label}
        {log.entity_id != null && (
          <span className="ml-1 text-[10px] text-blue-600">
            #{log.entity_id}
          </span>
        )}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-800 flex items-center">
            <ListChecks className="w-6 h-6 mr-2 text-orange-500" />
            Audit Log
          </h2>
          <p className="text-gray-600">
            System-wide record of who did what, when, and from where.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <form
          onSubmit={handleSearchSubmit}
          className="flex-1 flex items-center space-x-2"
        >
          <div className="relative flex-1">
            <Search className="w-4 h-4 text-gray-400 absolute left-3 top-2.5" />
            <input
              type="text"
              placeholder="Search by user, action, entity, IP..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
            />
          </div>
          <button
            type="submit"
            className="px-3 py-2 rounded-md bg-orange-500 text-white text-sm font-medium hover:bg-orange-600 transition"
          >
            Search
          </button>
        </form>

        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-1 text-xs text-gray-500">
            <Filter className="w-3 h-3" />
            <span>Filters:</span>
          </div>
          <select
            value={resultFilter}
            onChange={(e) => {
              setResultFilter(e.target.value);
              setPage(1);
            }}
            className="border rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
          >
            <option value="all">All results</option>
            <option value="success">Success</option>
            <option value="fail">Fail</option>
          </select>
          <select
            value={entityFilter}
            onChange={(e) => {
              setEntityFilter(e.target.value);
              setPage(1);
            }}
            className="border rounded-md px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-orange-500"
          >
            <option value="all">All entities</option>
            <option value="inspection">inspection</option>
            <option value="ncr">ncr</option>
            <option value="rfi">rfi</option>
            <option value="submittal">submittal</option>
            <option value="project">project</option>
            <option value="user">user</option>
          </select>
        </div>
      </div>

      {/* Body */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b px-4 py-3 flex items-center justify-between text-xs text-gray-500">
          <span>{total} events</span>
          {total > 0 && (
            <span>
              Page {page} of {totalPages}
            </span>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <LoadingSpinner />
          </div>
        ) : error ? (
          <div className="p-6 text-sm text-red-700 bg-red-50 border-t border-red-100">
            Failed to load audit log. Please try again.
          </div>
        ) : logs.length === 0 ? (
          <div className="p-6 text-sm text-gray-500">No audit events yet.</div>
        ) : (
          <div className="divide-y">
            {logs.map((log) => {
              const isExpanded = expandedId === log.id;
              return (
                <div
                  key={log.id}
                  className="px-4 py-3 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Timeline bullet */}
                    <div className="pt-1">
                      <span
                        className={`w-3 h-3 rounded-full inline-block ${
                          (log.result || "").toLowerCase() === "success"
                            ? "bg-emerald-500"
                            : (log.result || "").toLowerCase() === "fail"
                              ? "bg-red-500"
                              : "bg-gray-400"
                        }`}
                      />
                    </div>

                    {/* Main content */}
                    <div className="flex-1 space-y-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-semibold text-sm text-gray-900">
                          {log.actor_name || "System"}
                        </span>
                        {log.actor_role && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                            {log.actor_role}
                          </span>
                        )}
                        {resultsBadge(log.result)}
                        {entityBadge(log)}
                      </div>

                      <p className="text-sm text-gray-800">
                        <span className="font-medium">
                          {log.action_label || log.action}
                        </span>{" "}
                        {log.project_name && (
                          <>
                            on{" "}
                            <span className="font-semibold">
                              {log.project_name}
                            </span>
                          </>
                        )}
                      </p>

                      <div className="text-xs text-gray-500 flex flex-wrap gap-x-4 gap-y-1">
                        {log.actor_email && <span>{log.actor_email}</span>}
                        {log.ip && <span>IP: {log.ip}</span>}
                        {log.timestamp && (
                          <span>
                            {new Date(log.timestamp).toLocaleString()}
                          </span>
                        )}
                      </div>

                      {log.extra && Object.keys(log.extra).length > 0 && (
                        <button
                          type="button"
                          onClick={() =>
                            setExpandedId(isExpanded ? null : log.id)
                          }
                          className="mt-1 text-xs text-orange-600 hover:text-orange-700"
                        >
                          {isExpanded ? "Hide details" : "View details"}
                        </button>
                      )}

                      {isExpanded && log.extra && (
                        <pre className="mt-2 text-[11px] bg-gray-900 text-gray-100 rounded-md p-2 overflow-auto max-h-40">
                          {JSON.stringify(log.extra, null, 2)}
                        </pre>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Pagination footer */}
        {total > PAGE_SIZE && (
          <div className="border-t px-4 py-3 flex items-center justify-between text-xs text-gray-600">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className={`px-2 py-1 rounded-md border ${
                page <= 1
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Previous
            </button>
            <span>
              Page {page} of {totalPages}
            </span>
            <button
              type="button"
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              className={`px-2 py-1 rounded-md border ${
                page >= totalPages
                  ? "border-gray-200 text-gray-300 cursor-not-allowed"
                  : "border-gray-300 hover:bg-gray-50"
              }`}
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GlobalAuditLogView;
